
import { Component, Prop, Vue, Emit } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { Heading, Row, BaseText } from '@warrenbrasil/nebraska-web';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { SidebarErrorV2 } from '@/modules/trade/components/SidebarError';
import { HomeBrokerButton } from '../HomeBrokerButton';

import { SidebarLoader } from '@/modules/trade/components/SidebarLoader';
import { SidebarTypes } from '@/modules/trade/views/TradeView/components/TradeSidebar';
import { moneyFormat } from '@/modules/common/helpers/currency';
import {
  trackBlockedBalanceClick,
  trackAvailableToWithdrawClick,
  trackAvailableToInvestClick,
  trackBalancesSummaryRetryClick
} from '@/modules/trade/views/TradeView/trackers';
import {
  AccountStatus,
  Balances,
  HomeBrokerStatus
} from '@/modules/trade/services/account';
import { RequestStatus } from '@/modules/trade/store/trade';
import { useDashStore } from '@/modules/common/store/DashModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';

const tradePositionsModule = namespace('tradeModule');

@Component({
  components: {
    Heading,
    Row,
    BaseText,
    SidebarLoader,
    SidebarErrorV2,
    HomeBrokerButton
  },
  filters: {
    money: (value: number) => {
      return moneyFormat(value, true);
    }
  }
})
export default class Summary extends Vue {
  private readonly dashModule = useDashStore();

  @Prop({ type: Object, required: false })
  readonly balances?: Balances;

  @Prop({ type: Object, required: false })
  readonly homeBrokerStatus?: HomeBrokerStatus;

  @Prop({ type: Object, required: false })
  readonly tradeStatus?: AccountStatus;

  @tradePositionsModule.State('balancesBrokerRequestStatus')
  readonly balancesBrokerRequestStatus!: RequestStatus;

  @tradePositionsModule.Action('fetchBalances')
  readonly fetchBalances!: Function;

  private readonly sidebarTypes = SidebarTypes;
  readonly NebraskaColors = NebraskaColors;
  readonly RequestStatus = RequestStatus;

  @Emit('set-panel')
  setPanel(panel: SidebarTypes) {
    return panel;
  }

  @Emit('open-request-home-broker-modal')
  emitOpenRequestHomeBrokerModal() {}

  @Emit('open-home-broker')
  emitOpenHomeBroker() {}

  get shouldShowMoney() {
    return this.dashModule.showMoney;
  }

  get isFinOneIntegrationWithTradeV0Enabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_FIN_ONE_INTEGRATION_WITH_TRADE_V0
    );
  }

  get availableBalanceToInvest() {
    return this.balances?.availableBalanceToInvest?.formatted;
  }

  get hasProjectedBalances() {
    return (
      this.balances?.projectedBalances &&
      this.balances?.projectedBalances.length > 0
    );
  }

  get blockedBalance() {
    return this.balances?.blockedBalance?.formatted;
  }

  get hasBlockedBalance() {
    return Boolean(this.balances?.blockedBalance);
  }

  get isWithdrawalBalanceAvailable() {
    return (
      this.balances?.withdrawalBalance?.value &&
      this.balances.withdrawalBalance.value > 0
    );
  }

  get withdrawalBalance() {
    return this.balances?.withdrawalBalance?.formatted;
  }

  handleBalancePanel() {
    if (this.hasProjectedBalances) {
      trackAvailableToInvestClick();
      this.setPanel(this.sidebarTypes.SummaryInvested);
    }
  }

  handleBlockedBalancePanel() {
    trackBlockedBalanceClick();
    this.setPanel(this.sidebarTypes.BlockedBalance);
  }

  goToWithdrawl() {
    if (this.isWithdrawalBalanceAvailable) {
      trackAvailableToWithdrawClick();
      this.setPanel(SidebarTypes.SummaryWithdrawl);
    }
  }

  async handleSummaryRetryClick() {
    trackBalancesSummaryRetryClick();
    await this.fetchBalances();
  }
}
